import { backgroundOrange, black, blackOrange, gold, green, orange, vanilla } from '@/theme/colors'
import styled from 'styled-components'

export const FlightsPageContainer = styled.div`
    background-color: ${backgroundOrange};
    height: 100%;
    display: flex;
    flex-direction: column;
    /* The page scrolls; the centered ContentContainer stays a plain block so
       the wheel works across the full viewport width. */
    overflow-y: auto;
`

export const LoadingContainer = styled.div`
    display: flex;
    flex: 1;
    justify-content: center;
    align-items: center;
    background-color: ${backgroundOrange};
    height: 100%;
`

export const ErrorContainer = styled.div`
    padding: 2rem;
    text-align: center;
    color: red;
`

export const EmptyStateContainer = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: calc(100vh - 200px);
    padding: 2rem;
`

export const EmptyState = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 1.5rem;
    text-align: center;
`

export const EmptyStateIcon = styled.div`
    color: #000000;
    display: flex;
    align-items: center;
    justify-content: center;
`

export const EmptyStateText = styled.p`
    font-size: 1.125rem;
    color: #495057;
    margin: 0;
    max-width: 400px;
    line-height: 1.6;
`

export const EmptyStateButton = styled.button`
    background: ${orange};
    color: white;
    border: none;
    padding: 0.75rem 1.5rem;
    border-radius: 8px;
    font-size: 1rem;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover {
        opacity: 0.9;
        transform: translateY(-1px);
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
    }
`

export const ContentContainer = styled.div`
    /* Match the app's standard centered content width (files, overview, …);
       the 2.5rem top matches the overview's header→title breathing room. */
    width: 100%;
    max-width: 1400px;
    margin: 0 auto;
    padding: 2.5rem 1.5rem 1.5rem;

    @media (max-width: 600px) {
        padding: 1rem 0.875rem;
    }
`

export const HeaderRow = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 0.75rem;
    flex-wrap: wrap;
    margin-bottom: 1.5rem;
`

/** Main CTA orange — matches "+ Add Expense" / "+ Upload" on the other pages. */
export const AddButton = styled.button`
    background: ${orange};
    color: white;
    border: none;
    padding: 0.625rem 1.25rem;
    border-radius: 8px;
    font-size: 0.875rem;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s ease;
    display: flex;
    align-items: center;
    gap: 0.5rem;

    &:hover {
        opacity: 0.9;
        transform: translateY(-1px);
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
    }
`

export const FlightGroupGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(280px, 360px));
    gap: 1rem;
    justify-content: start;

    @media (max-width: 640px) {
        grid-template-columns: 1fr;
    }
`

export const FlightGroupCard = styled.div`
    background: white;
    border-radius: 12px;
    padding: 1rem 1.125rem;
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
    cursor: pointer;
    transition: all 0.2s ease;
    border: 1px solid #f0e0d6;

    &:hover {
        transform: translateY(-2px);
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
        border-color: ${orange}40;
    }
`

export const FlightGroupHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.5rem;
`

export const FlightGroupTitle = styled.div`
    font-size: 0.875rem;
    font-weight: 700;
    color: ${black};
`

export const ManualPill = styled.span`
    font-size: 0.625rem;
    font-weight: 600;
    color: white;
    background: ${blackOrange}cc;
    padding: 0.125rem 0.5rem;
    border-radius: 999px;
    white-space: nowrap;
`

export const LegsStack = styled.div`
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
`

export const LegRow = styled.div`
    display: flex;
    flex-direction: column;
    gap: 0.125rem;
    padding: 0.625rem 0.75rem;
    background: ${vanilla};
    border-radius: 8px;
`

export const LegRouteLine = styled.div`
    display: flex;
    align-items: baseline;
    gap: 0.5rem;
`

export const LegCode = styled.span`
    font-size: 0.875rem;
    font-weight: 700;
    color: ${black};
`

export const LegRoute = styled.span`
    font-size: 0.8125rem;
    font-weight: 500;
    color: ${blackOrange};
`

export const LegTimeText = styled.span`
    font-size: 0.75rem;
    color: #6b7280;
`

export const RouteBlock = styled.div`
    display: grid;
    grid-template-columns: 1fr auto 1fr;
    align-items: center;
    gap: 0.75rem;
    padding: 0.5rem 0 0.25rem;
`

export const RouteEndpoint = styled.div<{ $align: 'start' | 'end' }>`
    display: flex;
    flex-direction: column;
    align-items: ${({ $align }) => ($align === 'start' ? 'flex-start' : 'flex-end')};
    text-align: ${({ $align }) => ($align === 'start' ? 'left' : 'right')};
    gap: 0.25rem;
    min-width: 0;
`

export const RouteAirport = styled.span`
    font-size: 1.125rem;
    font-weight: 800;
    color: ${black};
    letter-spacing: 0.02em;
    line-height: 1;
`

export const RouteLabel = styled.span`
    font-size: 0.5625rem;
    font-weight: 700;
    color: ${gold};
    text-transform: uppercase;
    letter-spacing: 0.06em;
`

export const RouteTime = styled.span`
    font-size: 0.6875rem;
    font-weight: 600;
    color: #374151;
    white-space: nowrap;
`

export const RouteArrow = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    color: ${orange};
    position: relative;
    min-width: 48px;
    padding: 0 0.25rem;

    &::before,
    &::after {
        content: '';
        flex: 1;
        height: 0;
        border-top: 1.5px dashed ${orange}66;
        min-width: 10px;
    }

    svg {
        margin: 0 0.375rem;
        flex-shrink: 0;
    }
`

export const LayoverText = styled.div`
    font-size: 0.6875rem;
    font-weight: 500;
    color: ${gold};
    text-align: center;
    padding: 0.125rem 0;
`

export const PassengerRow = styled.div`
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding-top: 0.5rem;
    border-top: 1px solid #f5ebe5;
    flex-shrink: 0;
    min-height: 32px;
`

export const AvatarStack = styled.div`
    display: flex;
    align-items: center;
    flex-shrink: 0;
`

export const Avatar = styled.div<{ $index: number }>`
    width: 24px;
    height: 24px;
    border-radius: 50%;
    background: ${orange};
    color: white;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 0.5625rem;
    font-weight: 600;
    flex-shrink: 0;
    border: 2px solid white;
    margin-left: ${({ $index }) => ($index > 0 ? '-6px' : '0')};
    z-index: ${({ $index }) => 10 - $index};
    overflow: hidden;

    img {
        width: 100%;
        height: 100%;
        object-fit: cover;
    }
`

export const OverflowAvatar = styled(Avatar)`
    background: ${gold};
    font-size: 0.5rem;
`

export const PassengerCount = styled.span`
    font-size: 0.6875rem;
    color: #6b7280;
    font-weight: 500;
    white-space: nowrap;
`

export const YoureAPassengerPill = styled.span`
    font-size: 0.625rem;
    font-weight: 600;
    color: ${green};
    background: #e8f5e9;
    padding: 0.125rem 0.5rem;
    border-radius: 999px;
    white-space: nowrap;
    margin-left: auto;
    flex-shrink: 0;
`
